import { IonIcon } from "@ionic/react";
import { useRef } from "react";
import { type FieldApi } from "@tanstack/react-form";
import { closeOutline, informationCircleOutline } from "ionicons/icons";
import { ModalSheetAutoHeight } from "@/components/modal/sheet/auto-height";
import { cn } from "@/lib/style/cva.config";
import { ReadOnlyBadge } from "./read-only-badge";

export type CustomFieldMultiSelectProps = {
  label: string;
  description?: string;
  required?: boolean;
  /** #10a: render disabled/read-only (employee may not edit this field). */
  readOnly?: boolean;
  options: Array<{ label: string; value: string }>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  field: FieldApi<
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any
  >;
};

export function CustomFieldMultiSelect({
  label,
  description,
  required = false,
  readOnly = false,
  options,
  field,
}: CustomFieldMultiSelectProps) {
  const hasError = field.state.meta.errors && field.state.meta.errors.length > 0;
  // Extract error message - handle both string and ZodError object formats
  const errorMessage = hasError
    ? typeof field.state.meta.errors[0] === "string"
      ? field.state.meta.errors[0]
      : field.state.meta.errors[0]?.message || String(field.state.meta.errors[0])
    : null;
  const selectedValues: string[] = Array.isArray(field.state.value)
    ? (field.state.value as string[])
    : [];
  const infoModalRef = useRef<HTMLIonModalElement>(null);

  const toggleOption = (optionValue: string) => {
    if (readOnly) {
      return;
    }
    const newValues: string[] = selectedValues.includes(optionValue)
      ? selectedValues.filter((v) => v !== optionValue)
      : [...selectedValues, optionValue];
    field.handleChange(newValues);
  };

  return (
    <div className="flex flex-col gap-1.5">
      {/* Label */}
      <div className="flex items-center gap-1.5">
        <label className="text-md font-semibold text-txt-primary">
          {label}
          {required && <span className="text-error-120"> *</span>}
        </label>
        <ReadOnlyBadge readOnly={readOnly} />
        {description && (
          <button
            type="button"
            onClick={() => infoModalRef.current?.present()}
            className="flex h-6 w-6 items-center justify-center"
          >
            <IonIcon icon={informationCircleOutline} className="text-xl text-txt-tertiary" />
          </button>
        )}
      </div>

      {/* Checkbox options */}
      <div className="flex flex-col gap-1">
        {options.map((option) => {
          const isChecked = selectedValues.includes(option.value);

          return (
            <button
              key={option.value}
              type="button"
              disabled={readOnly}
              onClick={(e) => {
                e.preventDefault();
                toggleOption(option.value);
              }}
              className={cn(
                "flex w-full items-start justify-between rounded-md py-2 transition-colors hover:bg-surface-primary",
                readOnly && "cursor-not-allowed opacity-50 hover:bg-transparent",
              )}
            >
              <span className="text-md font-medium text-txt-primary">{option.label}</span>

              {/* Checkbox */}
              <div
                className={cn(
                  "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md border border-bordercolor-primary transition-colors",
                  isChecked ? "border-button-primary bg-button-primary" : "bg-white",
                )}
              >
                {isChecked && (
                  <svg
                    className="h-5 w-5 text-txt-inverted"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Error message */}
      {hasError && (
        <p className="text-sm font-medium leading-[1.4] text-error-120">{errorMessage}</p>
      )}

      {/* Info Modal */}
      {description && (
        <ModalSheetAutoHeight ref={infoModalRef}>
          <div className="flex max-h-[80vh] flex-col gap-5 px-6 pb-6 pt-4">
            {/* Header with title and close */}
            <div className="flex shrink-0 items-center justify-between">
              <h2 className="text-lg font-semibold leading-[1.6] text-txt-primary">{label}</h2>
              <button
                type="button"
                onClick={() => infoModalRef.current?.dismiss()}
                className="flex size-12 items-center justify-center"
              >
                <IonIcon icon={closeOutline} className="text-2xl text-txt-primary" />
              </button>
            </div>

            {/* Content - properly handles whitespace with whitespace-pre-line */}
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
              <p className="whitespace-pre-line text-md font-medium leading-[1.5] text-txt-tertiary">
                {description}
              </p>
            </div>

            {/* Close button */}
            <button
              type="button"
              onClick={() => infoModalRef.current?.dismiss()}
              className="w-full shrink-0 rounded-xl bg-button-primary py-3 text-center text-md font-semibold text-txt-inverted"
            >
              Begrepen
            </button>
          </div>
        </ModalSheetAutoHeight>
      )}
    </div>
  );
}
